import React from 'react';
import { CheckIcon, ArrowRightIcon, SparkleIcon, TrendingUpIcon, BadgeIcon } from './Icons';

/**
 * MasteryReport — gamified XP receipt (V2: positive reinforcement only).
 * Replaces RequirementsCard when allTestsPassing === true.
 *
 * Props:
 *   data — mastery_report object from the backend submit response
 *   onContinue — called when "Continue to Next Stage" is clicked
 */
export default function MasteryReport({ data, onContinue }) {
  if (!data) return null;

  const isExplore = data.exerciseType === 'explore';

  return isExplore
    ? <ExploreReceipt data={data} onContinue={onContinue} />
    : <StandardReceipt data={data} onContinue={onContinue} />;
}

// ── Explore receipt ───────────────────────────────────────────────────────────

function ExploreReceipt({ data, onContinue }) {
  const { discoveryBonus, totalXp, badges = [], masteryGained, masteryTopic, nextLesson } = data;
  const earnedCount = badges.filter(b => b.earned).length;

  return (
    <div className="mastery-report card">

      <div className="mr-hero">
        <span className="mr-sparkle mr-sparkle-1" aria-hidden="true"><SparkleIcon size={18} /></span>
        <span className="mr-sparkle mr-sparkle-2" aria-hidden="true"><SparkleIcon size={12} /></span>
        <div className="mr-hero-inner">
          <div className="mr-check-tile" aria-hidden="true">
            <CheckIcon size={18} />
          </div>
          <div>
            <div className="mr-passed-label">Exercise Explored</div>
            <div className="mr-nicely-done">Nice work.</div>
          </div>
        </div>
      </div>

      <div className="mr-body">
        <div className="mr-section-heading">XP Breakdown</div>
        <div className="mr-receipt">
          <ReceiptRow
            label="Discovery Bonus"
            sub="Explored the exercise"
            value={`+${discoveryBonus} XP`}
            variant="bonus"
          />
          <div className="mr-divider" />
          <ReceiptRow label="Total earned" value={`+${totalXp} XP`} variant="bold" />
        </div>

        {masteryTopic && (
          <div className="mr-mastery-pill">
            <div className="mr-mastery-icon" aria-hidden="true">
              <TrendingUpIcon size={18} />
            </div>
            <div className="mr-mastery-text">
              <span className="mr-mastery-label">Mastery gained</span>
              <span className="mr-mastery-value">
                <strong className="mr-mastery-num">+{masteryGained}</strong>
                <span className="mr-mastery-towards"> points towards </span>
                <code className="mr-mastery-topic ib-mono">{masteryTopic}</code>
              </span>
            </div>
          </div>
        )}

        {badges.length > 0 && (
          <div className="mr-badges-section">
            <div className="mr-section-heading">
              <span>Badges</span>
              {earnedCount > 0 && <span className="mr-badge-count">+{earnedCount}</span>}
            </div>
            <div className="mr-badges-grid">
              {badges.map(b => (
                <div
                  key={b.id}
                  className={`mr-badge-tile ${b.earned ? 'mr-badge-earned' : 'mr-badge-locked'}`}
                  title={b.name}
                >
                  <div className={`mr-badge-icon-wrap ${b.earned ? 'mr-badge-icon-earned' : ''}`}>
                    <BadgeIcon name={b.icon} size={15} />
                  </div>
                  <span className="mr-badge-name">{b.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mr-footer">
        <button className="mr-continue-btn" onClick={onContinue}>
          Continue to Next Stage <ArrowRightIcon size={15} />
        </button>
        {nextLesson && (
          <div className="mr-up-next">Up next · <strong>{nextLesson}</strong></div>
        )}
      </div>

    </div>
  );
}

// ── Standard receipt ──────────────────────────────────────────────────────────

function StandardReceipt({ data, onContinue }) {
  const {
    baseXp,
    hintsUsed,
    independenceBonus,
    firstTryBonus,
    testDriven,
    testDrivenMult,
    totalXp,
    badges = [],
    masteryGained,
    masteryTopic,
    nextLesson,
  } = data;

  const earnedCount = badges.filter(b => b.earned).length;

  return (
    <div className="mastery-report card">

      <div className="mr-hero">
        <span className="mr-sparkle mr-sparkle-1" aria-hidden="true"><SparkleIcon size={18} /></span>
        <span className="mr-sparkle mr-sparkle-2" aria-hidden="true"><SparkleIcon size={12} /></span>
        <div className="mr-hero-inner">
          <div className="mr-check-tile" aria-hidden="true">
            <CheckIcon size={18} />
          </div>
          <div>
            <div className="mr-passed-label">All Tests Passed</div>
            <div className="mr-nicely-done">Nicely done.</div>
          </div>
        </div>
      </div>

      <div className="mr-body">
        <div className="mr-section-heading">XP Breakdown</div>
        <div className="mr-receipt">
          <ReceiptRow label="Base XP" value={`+${baseXp} XP`} variant="positive" />

          {independenceBonus > 0 && (
            <ReceiptRow
              label="Independence Bonus"
              sub={hintsUsed === 0 ? 'No hints used' : '1 hint used'}
              value={`+${independenceBonus} XP`}
              variant="bonus"
            />
          )}

          {firstTryBonus > 0 && (
            <ReceiptRow
              label="First Try Bonus"
              sub="Passed on first submission"
              value={`+${firstTryBonus} XP`}
              variant="bonus"
            />
          )}

          {testDriven && (
            <ReceiptRow
              label="Test-Driven Bonus"
              sub={`${testDrivenMult}× multiplier · ran code before submitting`}
              value={`×${testDrivenMult}`}
              variant="bonus"
            />
          )}

          <div className="mr-divider" />
          <ReceiptRow label="Total earned" value={`+${totalXp} XP`} variant="bold" />
        </div>

        <div className="mr-mastery-pill">
          <div className="mr-mastery-icon" aria-hidden="true">
            <TrendingUpIcon size={18} />
          </div>
          <div className="mr-mastery-text">
            <span className="mr-mastery-label">Mastery gained</span>
            <span className="mr-mastery-value">
              <strong className="mr-mastery-num">+{masteryGained}</strong>
              <span className="mr-mastery-towards"> points towards </span>
              <code className="mr-mastery-topic ib-mono">{masteryTopic}</code>
            </span>
          </div>
        </div>

        {badges.length > 0 && (
          <div className="mr-badges-section">
            <div className="mr-section-heading">
              <span>Badges</span>
              {earnedCount > 0 && <span className="mr-badge-count">+{earnedCount}</span>}
            </div>
            <div className="mr-badges-grid">
              {badges.map(b => (
                <div
                  key={b.id}
                  className={`mr-badge-tile ${b.earned ? 'mr-badge-earned' : 'mr-badge-locked'}`}
                  title={b.name}
                >
                  <div className={`mr-badge-icon-wrap ${b.earned ? 'mr-badge-icon-earned' : ''}`}>
                    <BadgeIcon name={b.icon} size={15} />
                  </div>
                  <span className="mr-badge-name">{b.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mr-footer">
        <button className="mr-continue-btn" onClick={onContinue}>
          Continue to Next Stage <ArrowRightIcon size={15} />
        </button>
        {nextLesson && (
          <div className="mr-up-next">Up next · <strong>{nextLesson}</strong></div>
        )}
      </div>

    </div>
  );
}

// ── Shared ────────────────────────────────────────────────────────────────────

function ReceiptRow({ label, sub, value, variant }) {
  return (
    <div className="mr-receipt-row">
      <div className="mr-receipt-label-col">
        <span className={`mr-receipt-label ${variant === 'bold' ? 'mr-receipt-label-bold' : ''}`}>
          {label}
        </span>
        {sub && <span className="mr-receipt-sub">{sub}</span>}
      </div>
      <span className={`mr-receipt-value mr-value-${variant}`}>{value}</span>
    </div>
  );
}
